<script lang="ts" setup>
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useTableStore } from '@/store/storeTable'
import { useCellSave } from '@/composables/useCellSave'
import { useUserPreferencesStore } from "@/store/storeUserPreferences.js"

import BaseInput from "@/components/BaseComponents/BaseInput.vue"
import BaseProgressBar from "@/components/BaseComponents/BaseProgressBar.vue"

const userPreferencesStore = useUserPreferencesStore()
const { userPreferences } = storeToRefs(userPreferencesStore)

const props = defineProps({
  modelValue: {
    type: [String, Date],
    default: null
  },
  readOnly: {
    type: Boolean,
    default: false
  },
  singleLine: {
    type: Boolean,
    default: false
  },
  autoSave: {
    type: Boolean,
    default: true
  },
  rowId: { type: [String, Number], default: '' },
  header: { type: Object, required: true },
  onSaveSuccess: { type: Function, default: null },
  // US 3399: valgfri id som settes paa selve kontrollen, saa en etikett kan knyttes med for=.
  // Uten den erklaeringen ville Vues attribute-fallthrough lagt id-en paa rot-diven, og en
  // for=-peker dit navngir ingenting for en skjermleser selv om statisk analyse blir fornoeyd.
  id: { type: String, default: undefined }
})

const emit = defineEmits(['update:modelValue'])
// Lokal valideringstilstand (tidsformat/tom-verdi); lagringsfeil eies av useCellSave.
const hasError = ref(false)
const errorMessage = ref('')
const { saving, saveErrorMessage, saveCell } = useCellSave()
const tableStore = useTableStore()

const inputError = computed(() => {
  if (hasError.value) return errorMessage.value.trim() || 'Feil'
  return saveErrorMessage.value
})

const formattedValue = computed(() => {
  if (props.modelValue instanceof Date) {
    // Håndtering av Date-objekter
    return formatTime(props.modelValue);
  }

  if (typeof props.modelValue === 'string' && props.modelValue) {
    // Håndtering av datetime-strenger fra backend
    const parsedDate = parseTime(props.modelValue);
    if (parsedDate) {
      return formatTime(parsedDate);
    }
  }

  return props.modelValue; // Fall-back
});

function formatTime (date: Date | null): string {
  if (!date) return '';

  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  const seconds = date.getUTCSeconds();
  const milliseconds = date.getUTCMilliseconds();

  // Start med "HH:MM"
  let time = `${hours}:${minutes}`;

  // Legg til sekunder hvis de ikke er 0
  if (seconds > 0) {
    time += `:${seconds.toString().padStart(2, '0')}`;
  }

  // Legg til millisekunder hvis de finnes
  if (milliseconds > 0) {
    time += `.${milliseconds.toString().padStart(3, '0')}`;
  }

  return time;
}

// Split a compact time core (3-6 digits) into zero-padded hour/minute/second parts.
function compactCoreToParts (core: string): { hrs: string; mins: string; secs: string } {
  let hrs = '00'
  let mins = '00'
  let secs = '00'
  if (core.length === 3 || core.length === 4) {
    mins = core.slice(-2)
    hrs = core.slice(0, core.length - 2)
  } else if (core.length === 5 || core.length === 6) {
    secs = core.slice(-2)
    mins = core.slice(-4, -2)
    hrs = core.slice(0, core.length - 4)
  }
  if (hrs.length === 1) hrs = `0${hrs}`
  if (mins.length === 1) mins = `0${mins}`
  if (secs.length === 1) secs = `0${secs}`
  return { hrs, mins, secs }
}

// Expand a compact timepart like "1055" or "955" into "HH:MM[:SS[.fff]]". Returns the input
// unchanged when it already contains a colon or is not a compact numeric form.
function expandCompactTime (timepart: string): string {
  if (timepart.includes(':')) return timepart
  const compact = timepart.replaceAll(' ', '')
  const m = compact.match(/^(\d{3,6})([.,]\d{1,3})?$/)
  if (!m) return timepart
  const core = m[1]
  const frac = m[2]?.slice(1) || ''
  const { hrs, mins, secs } = compactCoreToParts(core)
  const ms = frac ? frac.padEnd(3, '0').slice(0, 3) : ''
  const msSuffix = ms ? `.${ms}` : ''
  return secs === '00' && !frac ? `${hrs}:${mins}` : `${hrs}:${mins}:${secs}${msSuffix}`
}

function parseTime (input: string): Date | null {
  if (!input) return null

  let [datepart, timepart] = input.split('T');
  if (!timepart) {
    timepart = datepart
  }
  // Allow compact formats like "1055" => 10:55, "955" => 09:55
  timepart = expandCompactTime(timepart)

  const [hours, minutes, secondsPart] = timepart.split(':')
  let seconds = 0
  let milliseconds = 0

  if (secondsPart) {
    const [secs, ms] = secondsPart.split('.')
    seconds = Number.parseInt(secs)
    milliseconds = ms ? Number.parseInt(ms) : 0
  }

  const hour = Number.parseInt(hours as string)
  const minute = Number.parseInt(minutes as string)
  if (hour > 23 || hour < 0 ||
    minute > 59 || minute < 0 ||
    seconds > 59 || seconds < 0) {
    console.warn('parse error: ', hour, minute, secondsPart);
    return null
  }

  return new Date(Date.UTC(1900, 1, 1,
    hour, minute, seconds, milliseconds))
}

async function changed (newValue: string | null) {
  if (newValue === '' || newValue === null) {
    if (props.header.isNullable) {
      emit('update:modelValue', null)
      if (!props.autoSave) return;
      const item = {
        tableKey: String(props.header?.tableKey || tableStore.tableKey || '').trim(),
        rowId: props.rowId,
        columnName: props.header.field,
        oldValue: props.modelValue,
        newValue: null
      };
      hasError.value = false
      errorMessage.value = ''
      await saveCell(item, props.onSaveSuccess as (() => void | Promise<void>) | null);
      return;
    } else {
      hasError.value = true;
      errorMessage.value = 'Feltet kan ikke være tomt';
      return;
    }
  }
  const modelValueAsDate = parseTime(newValue)

  if (!modelValueAsDate) {
    hasError.value = true
    errorMessage.value = 'Ugyldig tidsformat. Bruk HH:MM:SS.fff'
    return
  }

  emit('update:modelValue', modelValueAsDate)

  if (!props.autoSave) return

  const item = {
    tableKey: String(props.header?.tableKey || tableStore.tableKey || '').trim(),
    rowId: props.rowId,
    columnName: props.header.field,
    oldValue: props.modelValue,
    newValue: modelValueAsDate
  }

  hasError.value = false
  errorMessage.value = ''
  await saveCell(item, props.onSaveSuccess as (() => void | Promise<void>) | null);
}

</script>

<template>
  <div>
    <template v-if="readOnly">
      <div :class="{ 'text-right w-full': singleLine }" class="p-2">
        <template v-if="modelValue !== null && modelValue !== undefined && userPreferences">
          {{ formattedValue }}
        </template>
        <template v-else>
          <span class="italic text-fg-muted">(blank)</span>
        </template>
      </div>
    </template>
    <template v-else>
      <div class="w-full">
        <div class="relative flex items-center w-full">
          <BaseInput
            :id="id"
            :clearable="header.isNullable"
            :error="inputError"
            :model-value="formattedValue"
            class="w-full"
            type="text"
            @change="changed"
          />
        </div>
      </div>
      <BaseProgressBar v-if="saving"/>
      <div v-else class="h-1 m-0 p-0"/>
    </template>
  </div>
</template>
